<template>
  <div
    class="modelpublication-entry"
    :class="{ '-expired': expired, '-has-description': publication.description, '-has-date': endTime }"
  >
    <div class="modelpublication-entry__linkcontainer">
      <button
        type="button"
        class="button-icon"
        :title="t('actions.copy')"
        @click="copyPublicationLink(publication)"
      >
        <lucide-icon name="copy" />
      </button>
      <router-link
        :to="{
          path: `/p/${publication.id}${
            publication.startingArea ? `/${publication.startingArea.charAt(0)}` : ''
          }`
        }"
        target="_blank"
        class="modelpublication-entry__link"
      >
        <span>{{ protocol }}//</span><span>{{ domain }}</span>/p/{{ publication.id
        }}<template v-if="publication.startingArea"
          >/{{ publication.startingArea.charAt(0) }}</template
        >
      </router-link>
    </div>

    <formatted-text
      v-if="publication.description"
      class="modelpublication-entry__description"
      :value="publication.description"
    />
    <span
      v-if="endTime"
      class="modelpublication-entry__enddate"
      :title="timeStatus"
    >
      <template v-if="publicationTimeActive">
        <lucide-icon name="calendar" class="mr-1 modelpublication-entry__enddate-icon" />
      </template>
      <template v-else-if="expired">
        <lucide-icon name="calendar-x" class="mr-1 modelpublication-entry__enddate-icon" />
      </template>
      <template v-else>
        <lucide-icon name="calendar-clock" class="mr-1 modelpublication-entry__enddate-icon" />
      </template>
      <span @dblclick="emit('edit', publication.id)">
        <span
          v-if="startTime"
          class="modelpublication-entry__date"
          :title="formatPublicationDateTitle(startTime)"
          :class="{
            '-in-past': isInPast(startTime)
          }"
        >
          {{ formatPublicationDate(startTime, startTimeCustom) }}
        </span>
        – <span
          class="modelpublication-entry__date"
          :title="formatPublicationDateTitle(endTime)"
          :class="{
            '-in-past': isInPast(endTime)
          }"
        >
          {{ formatPublicationDate(endTime, endTimeCustom) }}
        </span>
      </span>
    </span>

    <ul class="modelpublication-entry__iconlist" @click.prevent="emit('edit', publication.id)">
      <li v-if="publication.hasPassword" class="modelpublication-entry__icon" :title="$t('labels.passwordProtected')">
        <lucide-icon name="lock" />
      </li>
      <li v-if="publication.isExampleModel" class="modelpublication-entry__icon" :title="$t('labels.isExampleModel')">
        <lucide-icon name="star" />
      </li>

      <li
        v-if="(publication.accessableAreas?.length ?? 5) < 5 || (publication.hiddenScenes?.length ?? 0) > 0"
        class="modelpublication-entry__icon"
        :title="$t('labels.hiddenAreas')"
      >
        <lucide-icon name="eye-off" />
      </li>

      <li v-if="publication.canFork" class="modelpublication-entry__icon" :title="$t('labels.canBeCopied')">
        <lucide-icon name="clipboard-check" />
      </li>
      <li v-else class="modelpublication-entry__icon" :title="$t('labels.cantBeCopied')">
        <lucide-icon name="clipboard-x" />
      </li>

    </ul>

    <div class="modelpublication-entry__actions">
      <button
        class="button-icon modelpublication-entry__action"
        type="button"
        @click="emit('edit', publication.id)"
      >
        <lucide-icon name="pencil" />
      </button>

      <button
        :title="$t('actions.deletePublication')"
        class="button-icon modelpublication-entry__action"
        type="button"
        @click="emit('delete', publication.id)"
      >
        <lucide-icon name="trash-2" />
      </button>
    </div>

    <!-- <pre>{{ publication }}</pre> -->
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { formatDate } from "@/i18n";
import { useToast } from "vue-toastification";
import { showError } from "@core/ui/alert/alert";

import FormattedText from "@core/ui/FormattedText.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import type { ModelPublication } from "@/api/ModelPublication";

const props = defineProps<{
  publication: ModelPublication;
}>();

const emit = defineEmits<{
  (e: "delete", publicationId: string): void;
  (e: "edit", publicationId: string): void;
}>();

const { t } = useI18n();
const toast = useToast();
const protocol = window.location.protocol;
const domain = window.location.hostname;

const startTime = computed(() => {
  if (!props.publication.startsAt) return null;
  return new Date(props.publication.startsAt);
});
const endTime = computed(() => {
  if (!props.publication.endsAt) return null;
  return new Date(props.publication.endsAt);
});
const startTimeCustom = computed(() => {
  if (!startTime.value) return false;
  if (isInPast(startTime.value)) return false;
  return (
    startTime.value.getHours() !== 0 ||
    startTime.value.getMinutes() !== 0
  );
});
const endTimeCustom = computed(() => {
  if (!endTime.value) return false;
  return (
    endTime.value.getHours() !== 23 ||
    endTime.value.getMinutes() !== 59
  );
});

const publicationTimeActive = computed(() => {
  const now = Date.now();
  if (startTime.value && now < startTime.value.getTime()) return false;
  if (endTime.value && now > endTime.value.getTime()) return false;
  return true;
});

function formatPublicationDate(date: Date, timeVisible = true) {
  const format = timeVisible ? "ff" : "DD";
  return formatDate(date, format);
}
function formatPublicationDateTitle(date: Date) {
  return formatDate(date, "fff");
}

function isInPast(date: Date | null) {
  if (!date) return false;
  return date.getTime() < Date.now();
}

const expired = computed(() => {
  if (!endTime.value) return false;
  return isDateExpired(endTime.value);
});

const timeStatus = computed(() => {
  const now = Date.now();
  if (startTime.value && now < startTime.value.getTime()) return t('status.pending');
  if (endTime.value && now > endTime.value.getTime()) return t('status.expired');
  return t('status.available');
});

function isDateExpired(date: Date | null) {
  if (!date) return false;
  return date.getTime() < Date.now();
}


async function copyPublicationLink(publication: ModelPublication) {
  const link = `${window.location.origin}/p/${publication.id}${publication.startingArea ? `/${publication.startingArea.charAt(0)}` : ''}`;
  try {
    await navigator.clipboard.writeText(link);
    toast.success(t("messages.linkCopiedToClipboard"));
  } catch (error) {
    console.warn("Failed to copy publication link:", (error as Error).message);
    showError(t("errors.couldNotCopyLink"));
  }
}
</script>

<style lang="scss">
.modelpublication-entry {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: 1fr min-content;
  grid-template-columns: subgrid;
  grid-template-rows: auto auto auto;
  grid-template-areas: "link link"
                       "date date"
                       "icons actions";
  gap: 1rem;
  row-gap: 0.25rem;
  align-items: center;
  padding: 0.25rem 1rem;
  padding-right: 0.5rem;
  background-color: var(--color-background-close);
  box-shadow: 0 1px 3px var(--color-shadow-1);

  &.-has-description {
    grid-template-rows: auto auto auto;
    grid-template-areas: "link link"
                        "description description"
                        "icons actions";
  }
  &.-has-date {
    grid-template-rows: auto auto auto;
    grid-template-areas: "link link"
                        "date date"
                        "icons actions";
  }
  &.-has-description.-has-date {
    grid-template-rows: auto auto auto auto;
    grid-template-areas: "link link"
                        "date date"
                        "description description"
                        "icons actions";
  }

  &.-expired {
    outline: 1px dashed var(--color-border-light);
    box-shadow: none;
  }

  @container(min-width: 700px) {
    grid-template-columns: 22rem 1fr min-content min-content;
    grid-template-columns: subgrid;
    grid-template-rows: auto;
    grid-template-areas: "link date icons actions";
    gap: 1.25rem;
    row-gap: 0;
    &:not(.-has-description) {
      grid-template-rows: auto;
      grid-template-areas: "link date icons actions";
    }
    &.-has-description, &.-has-date, &.-has-description.-has-date {
      grid-template-rows: auto auto;
      grid-template-areas: "link date icons actions"
                            "description description description description";
    }
  }

  &__iconlist {
    grid-area: icons;
    display: flex;
    gap: 0.5rem;
    list-style-type: none;
    padding: 0;
    margin: 0;
    @container(min-width: 700px) {
      justify-content: flex-end;
    }
  }
  &__icon,
  &__enddate-icon {
    font-size: 1.25em;
    color: var(--color-text-light);
  }

  &__actions {
    grid-area: actions;
    display: flex;
  }
  &__action {
    padding-inline: 0.5rem;
  }

  &__enddate {
    grid-area: date;
    display: flex;
    justify-content: flex-start;
    gap: 0.25rem;
    color: var(--color-text-medium);
    font-weight: 500;
    padding: 0;
    .-expired & {
      color: var(--color-error-dark);
    }

    @container(min-width: 700px) {
      padding: 0.25rem 0;
    }
  }
  &__date {
    white-space: nowrap;
    line-height: 1.3;
    &.-in-past {
      color: var(--color-text-light);
    }
  }

  &__link {
    display: block;
    min-width: 0;
    max-width: 22rem;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0.5rem 0;
    .-expired & {
      color: var(--color-text-light);
      text-decoration: line-through;
    }
  }
  &__linkcontainer {
    grid-area: link;
    display: flex;
    align-items: center;
    margin-left: -0.5rem;
    // background-color: var(--color-background);
    // box-shadow: inset 0 0 2px 1px var(--color-border);
    // border-radius: 4rem;
  }

  &__description {
    grid-area: description;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
    line-clamp: 5;
    padding-bottom: 0.25rem;

    max-height: 9.5em;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
